import React from "react";
import { Info, Plus } from "lucide-react";

import NoDataIcon from "../../../assets/svg/EmptyTableIcon";
import { ButtonV2 } from "../button/v2";
import { SaveButtonV2 } from "../button/v2/SaveButton";

export interface BaseDataTableFrameProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

export interface BaseDataTableEmptyStateProps {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  onAdd: () => void;
}

export interface BaseDataTableSaveFooterProps {
  isSaving: boolean;
  disabled: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export const BaseDataTableFrame: React.FC<BaseDataTableFrameProps> = ({
  title,
  subtitle,
  children,
  className = "",
}) => (
  <div className={`flex flex-col gap-2 w-full ${className}`}>
    <div className="flex flex-col gap-2">
      <span className="text-[12px] font-medium leading-none text-[#0A0A0A]">
        {title}
      </span>
      {subtitle && (
        <div className="flex items-center gap-1">
          <Info
            strokeWidth={1.6}
            className="w-4 h-4 text-[#737373] flex-shrink-0"
          />
          <span className="text-[12px] font-normal leading-none text-[#737373]">
            {subtitle}
          </span>
        </div>
      )}
    </div>

    <div className="w-full overflow-x-auto">{children}</div>
  </div>
);

export const BaseDataTableEmptyState: React.FC<
  BaseDataTableEmptyStateProps
> = ({
  title,
  subtitle = "Add records to populate this table",
  actionLabel = "Add Row",
  onAdd,
}) => (
  <div className="flex flex-col items-center justify-center py-8 px-4">
    <NoDataIcon />
    <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
      {title}
    </span>
    <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
      {subtitle}
    </span>
    <ButtonV2 onClick={onAdd}>
      <Plus className="w-4 h-4 mr-1" /> {actionLabel}
    </ButtonV2>
  </div>
);

export const BaseDataTableSaveFooter: React.FC<
  BaseDataTableSaveFooterProps
> = ({ isSaving, disabled, onSave, onDiscard }) => (
  <div className="bg-slate-100 flex flex-row-reverse gap-2 items-center p-2 rounded-md mt-4">
    <SaveButtonV2
      size="sm"
      buttonText="Save"
      loading={isSaving}
      disabled={disabled || isSaving}
      onClick={onSave}
    />

    <ButtonV2
      variant="ghost"
      size="sm"
      disabled={isSaving}
      onClick={onDiscard}
    >
      Cancel
    </ButtonV2>

    <span className="text-sm font-semibold leading-5 text-[#737373]">
      {isSaving
        ? "Saving changes..."
        : "You have made changes, save now"}
    </span>
  </div>
);

export default BaseDataTableFrame;
